/**
 * src/pages/WhatIsConsciousness/sections/NeuralLatticeVisual.tsx
 * Signature animated neural-network visual for the hero.
 * Renders pulsing synaptic nodes and traveling action potentials on a canvas.
 * Adapts to mobile and respects prefers-reduced-motion.
 */

import React, { useEffect, useRef } from "react";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
interface Node {
  x: number;
  y: number;
  baseRadius: number;
  pulsePhase: number;
  speed: number;
  connections: number[];
}
interface Pulse {
  fromNode: number;
  toNode: number;
  progress: number;
  speed: number;
}
export function NeuralLatticeVisual() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let isVisible = true;

    // Node topology
    const width = 340;
    const height = 340;
    const isMobile = window.innerWidth < 768;
    const nodeCount = isMobile ? 14 : 20;

    // Fixed deterministic coordinates relative to 340x340
    const rawNodes = [{
      x: 170,
      y: 70
    }, {
      x: 100,
      y: 110
    }, {
      x: 240,
      y: 110
    }, {
      x: 60,
      y: 180
    }, {
      x: 130,
      y: 170
    }, {
      x: 210,
      y: 170
    }, {
      x: 280,
      y: 180
    }, {
      x: 90,
      y: 240
    }, {
      x: 170,
      y: 230
    }, {
      x: 250,
      y: 240
    }, {
      x: 170,
      y: 140
    }, {
      x: 140,
      y: 290
    }, {
      x: 200,
      y: 290
    }, {
      x: 170,
      y: 320
    }, {
      x: 40,
      y: 130
    }, {
      x: 300,
      y: 130
    }, {
      x: 50,
      y: 220
    }, {
      x: 290,
      y: 220
    }, {
      x: 110,
      y: 60
    }, {
      x: 230,
      y: 60
    }].slice(0, nodeCount);
    const nodes: Node[] = rawNodes.map((pt, i) => {
      // Find nearest 3-4 neighbors
      const distances = rawNodes.map((other, idx) => ({
        idx,
        dist: Math.hypot(pt.x - other.x, pt.y - other.y)
      })).filter(d => d.idx !== i).sort((a, b) => a.dist - b.dist);
      return {
        x: pt.x,
        y: pt.y,
        baseRadius: 3 + i % 3,
        pulsePhase: i * Math.PI / 6,
        speed: 0.025 + i % 4 * 0.01,
        connections: distances.slice(0, 3).map(d => d.idx)
      };
    });
    const pulses: Pulse[] = [];
    for (let i = 0; i < (isMobile ? 5 : 9); i++) {
      const from = Math.floor(Math.random() * nodes.length);
      const conns = nodes[from].connections;
      const to = conns[Math.floor(Math.random() * conns.length)];
      pulses.push({
        fromNode: from,
        toNode: to,
        progress: Math.random(),
        speed: 0.008 + Math.random() * 0.012
      });
    }
    const observer = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
    });
    observer.observe(canvas);
    const render = () => {
      if (!isVisible) {
        if (!reducedMotion) {
          animationFrameId = requestAnimationFrame(render);
        }
        return;
      }
      ctx.clearRect(0, 0, width, height);

      // Draw faint connections
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(186, 230, 253, 0.18)"; // subtle cyan-blue
      nodes.forEach(node => {
        node.connections.forEach(targetIdx => {
          const target = nodes[targetIdx];
          if (!target) return;
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(target.x, target.y);
          ctx.stroke();
        });
      });

      // Draw active pulses
      pulses.forEach(p => {
        const from = nodes[p.fromNode];
        const to = nodes[p.toNode];
        if (!from || !to) return;
        const currentX = from.x + (to.x - from.x) * p.progress;
        const currentY = from.y + (to.y - from.y) * p.progress;

        // Glowing pulse particle
        const grad = ctx.createRadialGradient(currentX, currentY, 0, currentX, currentY, 6);
        grad.addColorStop(0, "rgba(255, 255, 255, 0.95)");
        grad.addColorStop(0.4, "rgba(56, 189, 248, 0.85)");
        grad.addColorStop(1, "rgba(168, 85, 247, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(currentX, currentY, 6, 0, Math.PI * 2);
        ctx.fill();
        if (!reducedMotion) {
          p.progress += p.speed;
          if (p.progress >= 1) {
            p.progress = 0;
            p.fromNode = p.toNode;
            const nextConns = nodes[p.fromNode].connections;
            p.toNode = nextConns[Math.floor(Math.random() * nextConns.length)];
          }
        }
      });

      // Draw nodes
      nodes.forEach(node => {
        if (!reducedMotion) {
          node.pulsePhase += node.speed;
        }
        const pulse = Math.sin(node.pulsePhase);
        const radius = node.baseRadius + pulse * 1.5;

        // Outer glow
        const glowGrad = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, radius * 2.8);
        glowGrad.addColorStop(0, "rgba(168, 85, 247, 0.5)"); // violet
        glowGrad.addColorStop(0.6, "rgba(56, 189, 248, 0.2)"); // cyan
        glowGrad.addColorStop(1, "rgba(56, 189, 248, 0)");
        ctx.fillStyle = glowGrad;
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius * 2.8, 0, Math.PI * 2);
        ctx.fill();

        // Node center
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(node.x, node.y, Math.max(2, radius * 0.8), 0, Math.PI * 2);
        ctx.fill();

        // Hairline boundary
        ctx.strokeStyle = "rgba(56, 189, 248, 0.9)";
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      if (!reducedMotion) {
        animationFrameId = requestAnimationFrame(render);
      }
    };
    render();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, [reducedMotion]);
  return <div className="relative flex items-center justify-center w-full max-w-[340px] min-w-0 aspect-square rounded-2xl p-2 bg-card/40 border border-primary/20 backdrop-blur-md shadow-[0_0_30px_-5px_var(--glow-violet)]">
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-secondary via-transparent to-accent pointer-events-none" />
      <canvas ref={canvasRef} width={340} height={340} className="w-full h-full block max-w-full touch-pan-y" aria-label="Interactive animated neural network showing synaptic firing across cortical nodes" />
      <div className="absolute bottom-2 inset-x-0 text-center pointer-events-none">
        <span className="font-mono text-[10px] tracking-widest uppercase text-primary/70 bg-background/80 px-2 py-0.5 rounded border border-border/40">
          Cortical Lattice Simulation
        </span>
      </div>
    </div>;
}